/**
 * ehr#437 part 4 (PROGRESS_NOTE_10-14-V81 Quick add, V50 / V61): phrase pills and dot phrases in a
 * note box. Pure, so the box (NoteField) and the tests read the same rules.
 */
import type { DotPhrase } from '@ehr/shared';
import { dischargePills } from './dischargeNote.js';
import { ADMIN_PHONE_PHRASES, isAdminPhoneKind } from './adminPhoneNote.js';
import { CASE_MANAGEMENT, CASE_MANAGEMENT_PHRASES } from './caseManagementNote.js';
import { COLLATERAL_PHRASES, isCollateral } from './collateralNote.js';
import { kindPhrasesFor } from './skillsMedNote.js';

/** V81 BOX_DEFAULTS phrases: the pills under each box until they're used. */
export const BOX_PHRASES: Record<string, readonly string[]> = {
  subjective: [
    'Client reported __ since the last session.',
    'Client rated anxiety __ out of 10.',
    'Client described __ as the main stressor.',
  ],
  objective: [
    'Client was alert and oriented to person, place and time.',
    'Affect was congruent with mood.',
    'Maintained good eye contact.',
  ],
  assessment: [
    'Client is making __ progress toward treatment goals.',
    'Insight and judgment were __.',
  ],
  plan: ['Next session is scheduled for __.', 'Client will practice __ between sessions.'],
  data: ['Client reported __ since the last session.', 'Affect was congruent with mood.'],
  behavior: ['Client presented as __.'],
  intervention: ['Clinician provided psychoeducation on __.'],
  response: [
    'Client was __ to the intervention.',
    'Client practiced __ in session.',
    'Client agreed to __ before next session.',
  ],
};

/**
 * ehr#713 (NO_SHOW_CANCELLATION_NOTE_10-29-V3 NS_PHRASES): the no-show note's Contact note pills,
 * no-show wording instead of the therapy pills.
 */
export const NO_SHOW_PHRASES: readonly string[] = [
  'Client did not attend the scheduled session and did not call ahead.',
  'Client cancelled with less than 24 hours notice. Reason given: __.',
  'Client cancelled with 24 hours notice or more. Reason given: __.',
  'Called the client at __ and left a voicemail to reschedule.',
  'Spoke with the client, who said __.',
  'Sent a secure message offering a new time.',
  'Rescheduled for __.',
  'Reminded the client of the cancellation policy.',
];

/**
 * The stock pills under a box: the no-show note's Contact note gets NO_SHOW_PHRASES (V3
 * set_phrases), a Collateral contact note's boxes COLLATERAL_PHRASES (ehr#728), a Skills / rehab
 * note's boxes the skills phrases (ehr#729); any other box keeps BOX_PHRASES only while it keeps
 * its stock label (a box a layout renamed, "Tests given", gets none).
 */
export function stockPillsFor(o: {
  key: string;
  label: string;
  stockLabel: string | undefined;
  kind: string;
}): readonly string[] {
  if (o.kind === 'No-show / cancellation' && o.key === 'subjective') return NO_SHOW_PHRASES;
  // ehr#708 (DISCHARGE_SUMMARY_NOTE_10-30-V6 set_phrases): discharge wording in its three boxes
  if (o.kind === 'Discharge summary') return dischargePills(o.key);
  // ehr#724 (ADMIN_PHONE_NOTE_10-30-V7): an Admin / phone note's Contact note gets the admin pills
  if (isAdminPhoneKind(o.kind) && o.key === 'subjective') return ADMIN_PHONE_PHRASES;
  // ehr#725 (CASE_MANAGEMENT_NOTE_10-30-V9): What happened and Next step get case management pills
  if (o.kind === CASE_MANAGEMENT) return CASE_MANAGEMENT_PHRASES[o.key] ?? [];
  // ehr#728: a Collateral contact note's What happened and Next step get the contact phrases
  if (isCollateral(o.kind)) return COLLATERAL_PHRASES[o.key] ?? [];
  const own = kindPhrasesFor(o.kind, o.key);
  if (own) return own;
  return o.stockLabel === o.label ? (BOX_PHRASES[o.key] ?? []) : [];
}

/** V81 PHRASE_PACKS: "+ Phrases" offers these, by approach. */
export const PHRASE_PACKS: { id: string; name: string; phrases: readonly string[] }[] = [
  {
    id: 'cbt',
    name: 'CBT',
    phrases: [
      'Clinician used cognitive restructuring to address __.',
      'Client completed a thought record for __.',
      'Client identified the cognitive distortion __.',
    ],
  },
  {
    id: 'dbt',
    name: 'DBT',
    phrases: [
      'Clinician taught the DBT skill __.',
      'Client practiced distress tolerance using __.',
      'Client reviewed the diary card for __.',
    ],
  },
  {
    id: 'mi',
    name: 'Motivational interviewing',
    phrases: [
      'Clinician used reflective listening to explore __.',
      'Client voiced change talk about __.',
      'Client rated readiness to change at __ out of 10.',
    ],
  },
  {
    id: 'trauma',
    name: 'Trauma',
    phrases: [
      'Clinician used grounding before processing __.',
      'Client tolerated the exposure exercise at SUDS __.',
      'Client identified a safe place image.',
    ],
  },
  {
    id: 'homework',
    name: 'Homework',
    phrases: [
      'Client agreed to practice __ before next session.',
      'Client completed __ of the assigned practice.',
      'Homework was reviewed and adjusted to __.',
    ],
  },
  {
    id: 'act',
    name: 'ACT',
    phrases: [
      'Clinician guided a defusion exercise for the thought __.',
      'Client identified the value __ to guide action.',
      'Client committed to the action step __.',
    ],
  },
  {
    id: 'family',
    name: 'Family',
    phrases: [
      'Clinician mapped the family pattern around __.',
      'Family members practiced __ in session.',
      'Client identified their role in __.',
    ],
  },
  {
    id: 'psychoed',
    name: 'Psychoeducation',
    phrases: [
      'Clinician provided psychoeducation on __.',
      'Client restated the key points about __.',
      'Client was given handouts on __.',
    ],
  },
  {
    id: 'telehealth',
    name: 'Telehealth',
    phrases: [
      'Session held by video; client was at __.',
      'Client confirmed a private location for the session.',
      'Audio and video were clear throughout.',
    ],
  },
];

/** V61: a pill leaves once its words are in the box; a __ blank matches whatever was typed there. */
export function phraseUsed(text: string, phrase: string): boolean {
  const t = text.toLowerCase().replace(/\s+/g, ' ');
  const p = phrase
    .toLowerCase()
    .replace(/\s+/g, ' ')
    .trim()
    .replace(/[.!?]+$/, '');
  if (!t || !p) return false;
  if (!p.includes('__')) return t.includes(p);
  const re = p
    .split('__')
    .map((x) => x.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'))
    .join('.+?');
  return new RegExp(re).test(t);
}

/** A phrase added to a box: after what's there, on the same line, spaced. */
export function withPhrase(text: string, phrase: string): string {
  const t = text.replace(/\s+$/, '');
  return t ? `${t} ${phrase}` : phrase;
}

export interface DotItem extends DotPhrase {
  source: 'mine' | 'practice';
}
/** The phrases a person sees, A to Z; their own replaces a practice phrase of the same name. */
export function dotListOf(practice: DotPhrase[], mine: DotPhrase[]): DotItem[] {
  const names = new Set(mine.map((p) => p.name));
  return [
    ...mine.map((p) => ({ ...p, source: 'mine' as const })),
    ...practice
      .filter((p) => !names.has(p.name))
      .map((p) => ({ ...p, source: 'practice' as const })),
  ].sort((a, b) => a.name.localeCompare(b.name));
}
/** Names that start with what was typed first, then names that contain it; at most 8. */
export function dotMatches(list: DotItem[], q: string): DotItem[] {
  const s = q.toLowerCase();
  return [
    ...list.filter((p) => p.name.startsWith(s)),
    ...list.filter((p) => p.name.indexOf(s) > 0),
  ].slice(0, 8);
}
/** ".nam" just before the cursor (after a space or at the start): where it starts and what was typed. */
export function dotTrigger(value: string, pos: number): { start: number; q: string } | null {
  const m = /(^|\s)\.([a-z][a-z0-9-]{0,19})$/i.exec(value.slice(0, pos));
  if (!m) return null;
  const q = m[2]!.toLowerCase();
  return { start: pos - q.length - 1, q };
}
/** {client}, {date}, {length} and {last plan} filled in; an unknown one becomes a __ blank. */
export function dotFill(
  text: string,
  v: { client?: string; date?: string; length?: string; lastPlan?: string },
): string {
  return text.replace(/\{(client|date|length|last plan)\}/gi, (_, k: string) => {
    const key = k.toLowerCase();
    const val =
      key === 'client'
        ? v.client
        : key === 'date'
          ? v.date
          : key === 'length'
            ? v.length
            : v.lastPlan;
    return val?.trim() ? val.trim() : '__';
  });
}
/** Where the next __ blank is from pos, or -1 (then Tab leaves the box as usual). */
export const nextBlank = (text: string, pos: number) => text.indexOf('__', pos);

// V81 PII: a sentence that looks like it holds client information never becomes a pill.
const PII = [
  /\b\d{3}[-.\s]\d{3}[-.\s]\d{4}\b/,
  /\b\d{1,2}\/\d{1,2}\/\d{2,4}\b/,
  /[^\s@]+@[^\s@]+\.[a-z]{2,}/i,
  /\b\d{3}-\d{2}-\d{4}\b/,
  /\b(dob|date of birth|ssn|medical record)\b/i,
];
/**
 * "From your notes" (V81 myPhrases): the sentences you write most often in this box, from your own
 * signed notes (at least 3 of them), most used first; at most 3. Never one naming the client.
 */
export function myPhrasesFrom(texts: string[], names: string[] = []): string[] {
  if (texts.length < 3) return [];
  const nameRes = names
    .filter((n) => n.trim().length > 1)
    .map((n) => new RegExp(`\\b${n.trim().replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}\\b`, 'i'));
  const count = new Map<string, number>();
  const first = new Map<string, number>();
  const shown = new Map<string, string>();
  texts.forEach((t, i) => {
    for (const raw of t.split(/(?<=[.!?])\s+/)) {
      const x = raw.trim();
      if (x.length < 12 || x.length > 120 || x.includes('__')) continue;
      if (PII.some((r) => r.test(x)) || nameRes.some((r) => r.test(x))) continue;
      const k = x.toLowerCase().replace(/[.!?]+$/, '');
      count.set(k, (count.get(k) ?? 0) + 1);
      if (!first.has(k)) {
        first.set(k, i);
        shown.set(k, /[.!?]$/.test(x) ? x : `${x}.`);
      }
    }
  });
  return [...count.keys()]
    .filter((k) => (count.get(k) ?? 0) > 1)
    .sort((a, b) => count.get(b)! - count.get(a)! || first.get(a)! - first.get(b)!)
    .slice(0, 3)
    .map((k) => shown.get(k)!);
}
